import React, { useState } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import OasisBoard from './OasisBoard.js';
import OasisBoardSkeleton from './OasisBoardSkeleton.js';
import { useAuth } from '../contexts/AuthContext.js';
import { OASIS_CATALOG, ANIMAL_COUNTS } from '../common/oasisCatalog.js';

const ADMIN_EMAIL = 'ccrane2@alumni.nd.edu';
const ALL_OASIS_IDS = Object.keys(OASIS_CATALOG).map(Number);
const COLUMN_CAPACITY = 3;

function shuffle(array) {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function randomInt(maxInclusive) {
  return Math.floor(Math.random() * (maxInclusive + 1));
}

function buildDeck() {
  const cards = [];
  let counter = 0;
  for (const [animal, count] of Object.entries(ANIMAL_COUNTS)) {
    for (let i = 0; i < count; i++) {
      cards.push({ cardId: `${animal}-${counter++}`, animal });
    }
  }
  return shuffle(cards);
}

function emptyOasisRow(ids) {
  return ids.map(oasisId => ({ oasisId, palmTreeOwnerIds: [], myColumn: [], opponentColumn: [] }));
}

// Builds a plausible mid-game state: a real 28-card deck is shuffled and
// dealt out (hands, an unseen round-removed card, and cards already placed
// on the board), so nothing here can ever show more copies of an animal
// than actually exist, and column sizes respect the normal capacity.
function generateRandomScenario() {
  const deck = buildDeck();
  let idx = 0;
  const take = n => {
    const slice = deck.slice(idx, idx + n);
    idx += n;
    return slice;
  };

  // Both players draw back up to 4 between turns, so a hand is almost
  // always full in a realistic mid-turn snapshot.
  const myHand = take(4);
  const opponentHandCount = 4;
  take(opponentHandCount); // opponent's real hand cards -- identities aren't ours to know
  take(1); // this round's face-down removed card, seen by no one

  const row = emptyOasisRow(shuffle(ALL_OASIS_IDS).slice(0, 5));

  const remaining = deck.slice(idx);
  const boardCards = remaining.slice(0, randomInt(remaining.length));
  for (const card of boardCards) {
    const columnKey = Math.random() < 0.5 ? 'myColumn' : 'opponentColumn';
    const openIndexes = row.map((_, i) => i).filter(i => row[i][columnKey].length < COLUMN_CAPACITY);
    if (openIndexes.length === 0) continue;
    const oasisIndex = openIndexes[randomInt(openIndexes.length - 1)];
    const faceDown = Math.random() < 0.2;
    // Face-up chameleons always need an orientation to point which
    // adjacent column they mimic.
    const orientation = !faceDown && card.animal === 'chameleon' ? (Math.random() < 0.5 ? 'left' : 'right') : null;
    if (columnKey === 'myColumn') {
      row[oasisIndex].myColumn.push({
        cardId: card.cardId,
        animal: card.animal,
        face: faceDown ? 'down' : 'up',
        orientation,
      });
    } else {
      row[oasisIndex].opponentColumn.push(
        faceDown
          ? { cardId: card.cardId, face: 'down' }
          : { cardId: card.cardId, animal: card.animal, face: 'up', orientation }
      );
    }
  }

  // Each player has at most 3 palm trees total, but only ever one placed on
  // the board at a time -- a single oasis card can have my palm tree, and a
  // single (possibly different) oasis card can have the opponent's.
  const myPalmTreesRemaining = randomInt(3);
  const opponentPalmTreesRemaining = randomInt(3);
  const palmSlots = shuffle(row.map((_, i) => i));
  let cursor = 0;
  if (myPalmTreesRemaining < 3 && randomInt(1) === 1) {
    row[palmSlots[cursor]].palmTreeOwnerIds.push('me');
    cursor += 1;
  }
  if (opponentPalmTreesRemaining < 3 && randomInt(1) === 1) {
    row[palmSlots[cursor]].palmTreeOwnerIds.push('opponent');
  }

  const round = randomInt(2) + 1; // 1-3
  const hasScores = round > 1;

  return {
    view: { hand: myHand, oasisRow: row },
    round,
    myScore: hasScores ? randomInt(15) : null,
    opponentScore: hasScores ? randomInt(15) : null,
    opponentHandCount,
    myPalmTreesRemaining,
    opponentPalmTreesRemaining,
    myTurn: Math.random() < 0.5,
    myName: 'You',
    opponentName: 'Computer',
  };
}

// A round-end flip has just revealed an ambiguity: the player has 2
// still-face-down Camels (a same-tier duplicate -- see
// functions/gameFlow.js's advanceRoundEndResolution), so it's their choice
// which one flips first. Static/reproducible on purpose (not randomized
// like the scenario below) so it's a stable target to test the flip-order
// UI in OasisBoard.js's RoundEndRow against.
function buildAmbiguousFlipScenario() {
  const row = emptyOasisRow([15, 5, 10, 3, 8]);
  row[0].myColumn = [{ cardId: 'scenario-camel-1', animal: 'camel', face: 'down' }];
  row[2].myColumn = [{ cardId: 'scenario-camel-2', animal: 'camel', face: 'down' }];
  // Already face-up nearby -- whichever duplicate camel is chosen to flip
  // first is adjacent to this one and would pull it in, so the choice
  // visibly changes the resulting board.
  row[1].myColumn = [{ cardId: 'scenario-camel-3', animal: 'camel', face: 'up' }];
  row[3].opponentColumn = [{ cardId: 'scenario-oryx-1', animal: 'oryx', face: 'up' }];
  row[4].myColumn = [{ cardId: 'scenario-jerboa-1', animal: 'jerboa', face: 'up' }];

  return {
    view: { hand: [], oasisRow: row },
    round: 1,
    myScore: null,
    opponentScore: null,
    opponentHandCount: 2,
    myPalmTreesRemaining: 3,
    opponentPalmTreesRemaining: 3,
    myTurn: false,
    myName: 'You',
    opponentName: 'Computer',
    roundEndResolution: {
      oasisRow: row,
      flippedCardIds: [],
      recentSteps: [],
      pendingStep: {
        kind: 'flipOrder',
        deciderSide: 'me',
        candidateCardIds: ['scenario-camel-1', 'scenario-camel-2'],
      },
    },
  };
}

// A freshly-dealt round 1 -- empty board, full starting hand, nothing
// scored yet. Static/reproducible on purpose (see /dev/design), so it's a
// stable target for checking the "start of game" look.
function buildEmptyRound1StartScenario() {
  const deck = buildDeck();
  const hand = deck.slice(0, 4);
  const row = emptyOasisRow(shuffle(ALL_OASIS_IDS).slice(0, 5));

  return {
    view: { hand, oasisRow: row },
    round: 1,
    myScore: null,
    opponentScore: null,
    opponentHandCount: 4,
    myPalmTreesRemaining: 3,
    opponentPalmTreesRemaining: 3,
    myTurn: true,
    myName: 'You',
    opponentName: 'Computer',
  };
}

// Round 1 fully resolved -- every column as full as the 28-card deck
// allows, both hands empty, the round summary showing. Static/reproducible
// (see /dev/design), so it's a stable target for checking the "full board"
// look, including the round-end reveal styling (win rings, burned card).
function buildFullBoardRoundEndScenario() {
  const deck = buildDeck();
  let idx = 0;
  const take = () => deck[idx++];

  const row = emptyOasisRow(shuffle(ALL_OASIS_IDS).slice(0, 5));
  for (const oasis of row) {
    for (const key of ['myColumn', 'opponentColumn']) {
      for (let i = 0; i < COLUMN_CAPACITY; i++) {
        const card = take();
        if (!card) break;
        oasis[key].push({
          cardId: card.cardId,
          animal: card.animal,
          face: 'up',
          orientation: card.animal === 'chameleon' ? 'right' : null,
        });
      }
    }
  }

  return {
    view: { hand: [], oasisRow: row },
    round: 1,
    myScore: 9,
    opponentScore: 6,
    opponentHandCount: 0,
    myPalmTreesRemaining: 2,
    opponentPalmTreesRemaining: 3,
    myTurn: false,
    myName: 'You',
    opponentName: 'Computer',
    revealResult: true,
    roundSummary: 'You won 9 - 6 this round.',
    burnedCard: { animal: 'oryx' },
  };
}

const SCENARIOS = [
  {
    key: 'ambiguous-flip',
    label: 'Ambiguous flipped cards at end of round',
    build: buildAmbiguousFlipScenario,
  },
  { key: 'random', label: 'Random mid-game board', build: generateRandomScenario },
  { key: 'empty-round1', label: 'Empty board, round 1 start', build: buildEmptyRound1StartScenario },
  { key: 'full-round1-end', label: 'Full board, end of round 1', build: buildFullBoardRoundEndScenario },
  // Not an OasisBoard scenario at all -- rendered specially below, see `skeleton`.
  { key: 'skeleton', label: 'Skeleton UI', build: () => null },
];

export default function BoardPlayground() {
  const { currentUser, authLoaded } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedKey = searchParams.get('scenario');
  const initialKey = SCENARIOS.some(s => s.key === requestedKey) ? requestedKey : SCENARIOS[0].key;
  const [scenarioKey, setScenarioKey] = useState(initialKey);
  const [scenario, setScenario] = useState(() => SCENARIOS.find(s => s.key === initialKey).build());

  if (!authLoaded) return <p>Loading…</p>;
  if (currentUser?.email !== ADMIN_EMAIL) return <Navigate to="/" replace />;

  function selectScenario(key) {
    setScenarioKey(key);
    setScenario(SCENARIOS.find(s => s.key === key).build());
    setSearchParams({ scenario: key });
  }

  function regenerate() {
    setScenario(SCENARIOS.find(s => s.key === scenarioKey).build());
  }

  return (
    <div className="panel w-100">
      <div className="mb-3">
        <Link to="/dev">← Dev</Link>
      </div>
      <h2 className="section-title mb-3">Board Playground (dev only)</h2>

      <div className="d-flex flex-wrap mb-3" style={{ gap: '0.5rem' }}>
        {SCENARIOS.map(s => (
          <Button
            key={s.key}
            variant={scenarioKey === s.key ? 'primary' : 'outline-primary'}
            onClick={() => selectScenario(s.key)}
          >
            {s.label}
          </Button>
        ))}
        <Button variant="outline-primary" onClick={regenerate}>
          Regenerate
        </Button>
      </div>

      {scenarioKey === 'skeleton' ? (
        <OasisBoardSkeleton />
      ) : (
        <OasisBoard
          {...scenario}
          onSubmitMove={payload => console.log('submitMove payload:', payload)}
          onSubmitRoundEndChoice={(kind, choice) => console.log('roundEndChoice:', kind, choice)}
          submitting={false}
        />
      )}
    </div>
  );
}
